import { call, put, select, take } from 'redux-saga/effects';
import { push } from '../../../lib/redux-router';

import request from '../request';
import selectors from '../../../selectors';
import actions from '../../../actions';
import api from '../../../api';
import ActionTypes from '../../../constants/ActionTypes';
import Paths from '../../../constants/Paths';
import orm from '../../../orm';

export function* goToRoot() {
  yield put(push(Paths.ROOT));
}

export function* goToProject(projectId) {
  yield put(push(Paths.PROJECTS.replace(':id', projectId)));
}

export function* goToBoard(boardId) {
  yield put(push(Paths.BOARDS.replace(':id', boardId)));
}

export function* goToCard(cardId) {
  yield put(push(Paths.CARDS.replace(':id', cardId)));
}

export function* handleLocationChange() {
  const pathsMatch = yield select(selectors.selectPathsMatch);

  if (!pathsMatch) {
    return;
  }

  switch (pathsMatch.pattern.path) {
    case Paths.LOGIN:
    case Paths.OIDC_CALLBACK:
      yield call(goToRoot);

      return;
    default:
  }

  const isInitializing = yield select(selectors.selectIsInitializing);

  if (isInitializing) {
    yield take(ActionTypes.CORE_INITIALIZE);
  }

  let board;
  let users;
  let projects;
  let boardMemberships;
  let labels;
  let lists;
  let cards;
  let cardMemberships;
  let cardLabels;
  let tasks;
  let attachments;
  let deletedNotifications;
  let cardPointData;
  let cardPointAdvancedData;
  let cardPointArchivedData;
  let listReportData;
  let contentCardDefaults;
  let contentTypes;
  let pointSystems;

  switch (pathsMatch.pattern.path) {
    case Paths.BOARDS:
    case Paths.CARDS: {
      const currentBoard = yield select(selectors.selectCurrentBoard);

      // isFetching: null — ещё не грузили; true — загрузка (в т.ч. застрявшая).
      // false — уже успешно загружена. Раньше true пропускали → спиннер навсегда.
      if (currentBoard && currentBoard.isFetching !== false) {
        if (currentBoard.isFetching === null) {
          yield put(actions.handleLocationChange.fetchBoard(currentBoard.id));
        }

        try {
          const response = yield call(request, api.getBoard, currentBoard.id, true);
          console.log('[handleLocationChange] Ответ от API getBoard:', response);
          ({
            item: board,
            included: {
              users,
              projects,
              boardMemberships,
              labels,
              lists,
              cards,
              cardMemberships,
              cardLabels,
              tasks,
              attachments,
              cardPointData,
              cardPointAdvancedData,
              cardPointArchivedData,
              listReportData,
              contentCardDefaults,
              contentTypes,
              pointSystems,
            },
          } = response);
          pointSystems = pointSystems || [];
          console.log('[handleLocationChange] Извлеченные данные - board:', board, 'pointSystems:', pointSystems);
        } catch (error) {
          console.error('[handleLocationChange] Ошибка при загрузке board:', error);
          yield put(actions.fetchBoard.failure(currentBoard.id, error));
        }
      } else {
        console.log('[handleLocationChange] currentBoard не найден или уже загружен, currentBoard:', currentBoard);
      }

      if (pathsMatch.pattern.path === Paths.CARDS) {
        const notificationIds = yield select(selectors.selectNotificationIdsForCurrentCard);

        if (notificationIds && notificationIds.length > 0) {
          try {
            ({ items: deletedNotifications } = yield call(
              request,
              api.updateNotifications,
              notificationIds,
              {
                isRead: true,
              },
            ));
          } catch (error) {} // eslint-disable-line no-empty
        }
      }

      break;
    }
    default:
  }

  yield put(
    actions.handleLocationChange(
      board,
      users,
      projects,
      boardMemberships,
      labels,
      lists,
      cards,
      cardMemberships,
      cardLabels,
      tasks,
      attachments,
      deletedNotifications,
      cardPointData || [],
    ),
  );

  // Важно: сначала кладём PointSystem в ORM, иначе селекторы ниже вернут null,
  // и cardPointData/listReportData/... будут пропущены (без повторной обработки).
  // pointSystems поднимается из included (или берётся из Redux fallback).
  if (!pointSystems || pointSystems.length === 0) {
    const currentBoard = yield select(selectors.selectCurrentBoard);
    if (currentBoard) {
      const pointSystemFromRedux = yield select(selectors.selectPointSystemForCurrentBoard);
      if (pointSystemFromRedux) {
        console.log(
          '[handleLocationChange] pointSystems не загружены из API, используем из Redux:',
          pointSystemFromRedux,
        );
        pointSystems = [pointSystemFromRedux];
      }
    }
  }

  if (pointSystems && pointSystems.length > 0) {
    for (const pointSystem of pointSystems) {
      yield put(actions.updatePointSystem.success(pointSystem));
    }
  }

  // Обрабатываем cardPointData для детекции закрытости задач
  if (cardPointData && cardPointData.length > 0) {
    const pointSystem = yield select(selectors.selectPointSystemForCurrentBoard);
    if (pointSystem) {
      for (const item of cardPointData) {
        if (item) {
          yield put(actions.fetchCardPointData.success(item.cardId, pointSystem.id, item));
        }
      }
    }
  }

  // Обрабатываем cardPointAdvancedData для подсчета суммы баллов
  if (cardPointAdvancedData && cardPointAdvancedData.length > 0) {
    const pointSystem = yield select(selectors.selectPointSystemForCurrentBoard);
    if (pointSystem) {
      // Группируем по cardId для обработки
      const cardPointAdvancedDataByCardId = {};
      cardPointAdvancedData.forEach((item) => {
        if (!cardPointAdvancedDataByCardId[item.cardId]) {
          cardPointAdvancedDataByCardId[item.cardId] = [];
        }
        cardPointAdvancedDataByCardId[item.cardId].push(item);
      });

      // Обрабатываем каждую карточку
      for (const cardId of Object.keys(cardPointAdvancedDataByCardId)) {
        const items = cardPointAdvancedDataByCardId[cardId];
        yield put(actions.fetchCardPointAdvancedData.success(cardId, pointSystem.id, items));
      }
    }
  }

  // Обрабатываем cardPointArchivedData для корректного отображения task_closed_at (taskClosedAt)
  // и для логики автоматического закрытия/сброса месяца
  if (cardPointArchivedData && cardPointArchivedData.length > 0) {
    for (const item of cardPointArchivedData) {
      if (item) {
        // Используем action для создания/обновления CardPointArchivedData
        // Это тот же action, что используется при закрытии задачи
        yield put({
          type: ActionTypes.CARD_POINT_DATA_COMPLETE__SUCCESS,
          payload: {
            archivedItem: item,
          },
        });
      }
    }
  }

  // Обрабатываем listReportData для отображения статуса закрытия месяца
  if (listReportData && listReportData.length > 0) {
    for (const item of listReportData) {
      if (item) {
        yield put(actions.fetchListReportData.success(item.listId, item));
      }
    }
  }

  // Обрабатываем contentCardDefaults для подсчета суммы баллов
  if (contentCardDefaults && contentCardDefaults.length > 0) {
    const pointSystem = yield select(selectors.selectPointSystemForCurrentBoard);
    if (pointSystem) {
      yield put(actions.fetchContentCardDefaults.success(pointSystem.id, contentCardDefaults));
    }
  }

  if (contentTypes && contentTypes.length > 0) {
    const pointSystem = yield select(selectors.selectPointSystemForCurrentBoard);
    if (pointSystem) {
      yield put(actions.fetchContentTypes.success(pointSystem.id, contentTypes));
    }
  }

  // Сохраняем pointSystems в Redux и загружаем DampingCoefficients
  try {
  if (pointSystems && pointSystems.length > 0) {
    const currentBoard = yield select(selectors.selectCurrentBoard);
    const boardId = board?.id || currentBoard?.id;
    
    if (boardId) {
      console.log('[handleLocationChange] Сохраняем pointSystems:', pointSystems);

      // Находим pointSystem для проекта текущей доски
      const currentProjectId = board?.projectId || currentBoard?.projectId;
      const pointSystem = pointSystems.find(
        (ps) => String(ps.projectId) === String(currentProjectId),
      );
      if (pointSystem) {
        const pointSystemId = pointSystem.id;
        console.log('[handleLocationChange] Найден pointSystem, загружаем данные для подсчета баллов для pointSystemId:', pointSystemId);
        
        // Загружаем DampingCoefficients
        const dampingCoefficientsService = yield import('./damping-coefficients');
        yield call(dampingCoefficientsService.fetchDampingCoefficientsInCurrentBoard, pointSystemId);
        
        // Загружаем ContentViewPlans для расчета коэффициентов
        const contentViewPlansService = yield import('./content-view-plans');
        yield call(contentViewPlansService.fetchContentViewPlansInCurrentBoard, pointSystemId);
        
        // Загружаем EmployeePlans для расчета коэффициентов
        const employeePlansService = yield import('./employee-plans');
        yield call(employeePlansService.fetchEmployeePlansInCurrentBoard, pointSystemId);
        
        // Проверяем, что данные действительно загружены в ORM
        const state = yield select();
        const session = orm.session(state.orm);
        const dampingCoefficientsInOrm = session.DampingCoefficient.all()
          .filter({ pointSistemId: pointSystemId })
          .toRefArray();
        console.log('[handleLocationChange] DampingCoefficients в ORM после загрузки:', dampingCoefficientsInOrm.length);
        
        const contentViewPlansInOrm = session.ContentViewPlan.all()
          .filter({ pointSistemId: pointSystemId })
          .toRefArray();
        console.log('[handleLocationChange] ContentViewPlans в ORM после загрузки:', contentViewPlansInOrm.length);
        
        const employeePlansInOrm = session.EmployeePlan.all()
          .filter({ pointSystemId: pointSystemId })
          .toRefArray();
        console.log('[handleLocationChange] EmployeePlans в ORM после загрузки:', employeePlansInOrm.length);
      } else {
        console.log(
          '[handleLocationChange] pointSystem не найден для projectId:',
          currentProjectId,
          'pointSystems:',
          pointSystems,
        );
      }
    } else {
      console.log('[handleLocationChange] boardId не найден, board:', board, 'currentBoard:', currentBoard);
    }
  } else {
    console.log('[handleLocationChange] pointSystems отсутствуют, pointSystems:', pointSystems);
  }
  } catch (error) {
    console.error('[handleLocationChange] Ошибка загрузки данных балльной системы:', error);
  }
}

export default {
  goToRoot,
  goToProject,
  goToBoard,
  goToCard,
  handleLocationChange,
};
